"use client";

import { Star, DollarSign } from "lucide-react";
import type { QuoteResultCardData } from "../../types/insurabot-cards";

const S = "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px, rgba(0,0,0,0.02) 0px 4px 12px -2px";

export default function QuoteResultCard({ data }: { data: QuoteResultCardData }) {
  if (!data.quotes || data.quotes.length === 0) return null;

  const sorted = [...data.quotes].sort((a, b) => a.monthlyPremium - b.monthlyPremium);
  const cheapest = sorted[0]?.carrier;

  return (
    <div className="mt-3 rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ boxShadow: S }}>
      <div className="bg-white px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em]">Quote Results</p>
          <span className="text-[10px] text-gray-500">{data.quotes.length} {data.quotes.length === 1 ? "quote" : "quotes"}</span>
        </div>

        {data.applicantSummary && (
          <p className="text-[11px] text-gray-500 mb-3">{data.applicantSummary}</p>
        )}

        {/* Scrollable quote cards */}
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }}>
          {sorted.map((quote, i) => {
            const isBest = quote.carrier === cheapest;
            return (
              <div
                key={i}
                className="flex-shrink-0 rounded-lg p-3 min-w-[140px]"
                style={{
                  boxShadow: isBest ? `rgba(5,150,105,0.15) 0px 0px 0px 2px, ${S}` : S,
                  backgroundColor: isBest ? "#f0fdf4" : "white",
                }}
              >
                {isBest && (
                  <span className="text-[7px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded mb-2 inline-block">Best Value</span>
                )}
                <p className="text-[10px] font-semibold text-gray-900 truncate">{quote.carrier}</p>
                <p className="text-[9px] text-gray-400 truncate mb-2">{quote.product}</p>
                <p className="text-xl font-mono font-black text-gray-900 leading-none" style={{ fontFeatureSettings: '"tnum"' }}>
                  <span className="text-emerald-600">${quote.monthlyPremium.toFixed(2)}</span>
                  <span className="text-xs text-gray-400 font-medium">/mo</span>
                </p>
                <p className="text-[9px] text-gray-400 font-mono mt-1" style={{ fontFeatureSettings: '"tnum"' }}>
                  ${quote.annualPremium.toFixed(2)}/yr
                </p>
                {quote.amBestRating && (
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="h-2.5 w-2.5 text-amber-500" />
                    <span className="text-[8px] font-bold text-gray-500">{quote.amBestRating}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
